import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchStateData,
  fetchDistrictData,
  createCity,
} from "../Services/APIService";

interface CreateCityCardProps {
  onCreateComplete: () => void;
  onCancel: () => void;
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface District {
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface FormData {
  cityName: string;
  stateId: number | null;
  districtId: number | null;
}

const CreateCityCard: React.FC<CreateCityCardProps> = ({
  onCreateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    cityName: "",
    stateId: null,
    districtId: null,
  });

  const [states, setStates] = useState<State[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const requiredField = <span className="text-danger ms-1">*</span>;

  // Fetch states and districts on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingStates(true);
      setIsLoadingDistricts(true);

      try {
        // Fetch states
        const stateData = await fetchStateData();
        setStates(stateData.filter((state)=>state.isActive));

        // Set default state if available
        if (stateData.length > 0) {
          setFormData((prev) => ({
            ...prev,
            stateId: stateData[0].stateId,
          }));
        }

        // Fetch all districts using fetchDistrictData from APIService
        const districtData = await fetchDistrictData();
        setAllDistricts(districtData.filter((dist)=>dist.isActive));
      } catch (error) {
        setError("Failed to fetch data");
        console.error(error);
      } finally {
        setIsLoadingStates(false);
        setIsLoadingDistricts(false);
      }
    };

    fetchData();
  }, []);

  // Filter districts when state is selected or district data changes
  useEffect(() => {
    if (formData.stateId && allDistricts.length > 0) {
      const districts = allDistricts.filter(
        (district) => district.stateId === formData.stateId
      );

      setFilteredDistricts(districts);

      // Set default district if available
      if (districts.length > 0 && !formData.districtId) {
        setFormData((prev) => ({
          ...prev,
          districtId: districts[0].districtId,
        }));
      } else if (districts.length === 0) {
        // Reset district selection if no districts available for the selected state
        setFormData((prev) => ({
          ...prev,
          districtId: null,
        }));
      }
    } else {
      setFilteredDistricts([]);
      setFormData((prev) => ({
        ...prev,
        districtId: null,
      }));
    }
  }, [formData.stateId, allDistricts]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Handle special case for state change to reset district
    if (name === "stateId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
        districtId: null, // Reset district when state changes
      }));
    } else if (name === "districtId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.cityName.trim()) {
      setError("Please enter a valid City Name");
      return;
    }

    if (!formData.stateId) {
      setError("Please select a State");
      return;
    }

    if (!formData.districtId) {
      setError("Please select a District");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID and tenant ID from localStorage
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      // Prepare request payload according to the specified format
      const payload = {
        cityName: formData.cityName.trim().replace(/\s+/g, " "),
        stateID: formData.stateId,
        districtID: formData.districtId,
        tenantId: parseInt(tenantId),
        createdBy: parseInt(userId),
      };

      const result = await createCity(payload);

      if (result.isCreated) {
        toast.success(result.message || "City Created Successfully!");
        // Reset form after successful submission
        setFormData({
          cityName: "",
          stateId: states.length > 0 ? states[0].stateId : null,
          districtId:
            filteredDistricts.length > 0
              ? filteredDistricts[0].districtId
              : null,
        });
        // Call the refresh function
        onCreateComplete();
      } else {
        throw new Error(result.message || "Failed to create city");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({ cityName: "", stateId: null, districtId: null });
    setError(null);
    // Call the cancel function
    onCancel();
  };

  return (
    <div className="card mb-4">
      
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create City</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="stateId" className="form-label">
                State Name{requiredField}
              </label>
              {isLoadingStates ? (
                <div className="text-muted">Loading states...</div>
              ) : (
                <select
                  id="stateId"
                  name="stateId"
                  className="form-select form-control-lg"
                  value={formData.stateId ?? ""}
                  onChange={handleInputChange}
                  required
                >
                  {states.length === 0 ? (
                    <option value="" disabled>
                      No states available
                    </option>
                  ) : (
                    states.map((state) => (
                      <option key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="districtId" className="form-label">
                District Name{requiredField}
              </label>
              {isLoadingDistricts ? (
                <div className="text-muted">Loading districts...</div>
              ) : (
                <select
                  id="districtId"
                  name="districtId"
                  className="form-select form-control-lg"
                  value={formData.districtId ?? ""}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.stateId || filteredDistricts.length === 0}
                >
                  {filteredDistricts.length === 0 ? (
                    <option value="" disabled>
                      {formData.stateId
                        ? "No districts available for selected state"
                        : "Select a state first"}
                    </option>
                  ) : (
                    filteredDistricts.map((district) => (
                      <option
                        key={district.districtId}
                        value={district.districtId}
                      >
                        {district.districtName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="cityName" className="form-label">
                City Name{requiredField}
              </label>
              <input
                type="text"
                id="cityName"
                name="cityName"
                className="form-control form-control-lg"
                placeholder="Enter City Name"
                value={formData.cityName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-12 mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || isLoadingStates || isLoadingDistricts}
              >
                {isLoading ? "Creating..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary mx-2"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCityCard;
